import keyBy from 'lodash.keyby';
import get from 'lodash.get';
import omit from 'lodash.omit';
import mapValues from 'lodash.mapvalues';
import { OutputParametricSelector } from 'reselect';

import { AppState } from 'services/types';
// @ts-ignore
import { selectActiveTableCellRowIds } from 'services/selectedTableCells/selectors';
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
import formatDropdownOptions from 'utils/format-dropdown-options';

import { FIELD_TYPE_PASSWORD } from 'components/VirtualizedTable/constants';

import { VIRTUALIZED_TABLE_COLUMN_MIN_WIDTH } from './constants';
import {
    VirtualizedTableColumn,
    VirtualizedTableColumnKey,
    VirtualizedTableItemKey,
    VirtualizedTableState,
    VirtualizedTableProps,
    VirtualizedTableItemValue
} from './types';

const selectVirtualizedTable = (state: AppState): VirtualizedTableState =>
    state.virtualizedTable;

const selectVirtualizedTableColumnKey = (
    state: AppState,
    props: VirtualizedTableProps
): VirtualizedTableColumnKey | undefined => props.columnKey;

const selectVirtualizedTableItemKey = (
    state: AppState,
    props: VirtualizedTableProps
): VirtualizedTableItemKey | undefined => {
    return props.itemKey;
};

const selectVirtualizedTableColumnKeys = createDeepEqualSelector(
    [selectVirtualizedTable],
    (virtualizedTable) => virtualizedTable.columnKeys
);

const selectVirtualizedTableColumnsKeyedByKey = createDeepEqualSelector(
    [selectVirtualizedTable],
    (virtualizedTable) => virtualizedTable.columnsKeyedByKey
);

const selectVirtualizedTableColumnsArray = createDeepEqualSelector(
    [selectVirtualizedTableColumnsKeyedByKey],
    (virtualizedTableColumnsKeyedByKey) =>
        Object.values(virtualizedTableColumnsKeyedByKey)
);

const selectVirtualizedTableColumnsWithValidators = createDeepEqualSelector(
    [selectVirtualizedTableColumnsArray],
    (virtualizedTableColumnsArray) =>
        virtualizedTableColumnsArray.filter((column) => column.validators)
);

const selectVirtualizedTableColumnOptionsKeyedByKey = createDeepEqualSelector(
    [selectVirtualizedTableColumnsKeyedByKey],
    (virtualizedTableColumnsKeyedByKey) =>
        mapValues(virtualizedTableColumnsKeyedByKey, (column) => {
            if (column.options) {
                return keyBy(column.options, 'key');
            }
            return undefined;
        })
);

const selectVirtualizedTableErrors = createDeepEqualSelector(
    [selectVirtualizedTable],
    (virtualizedTable) => virtualizedTable.errors
);

const selectVirtualizedTableErrorsExist = createDeepEqualSelector(
    [selectVirtualizedTable],
    (virtualizedTable) => !!Object.keys(virtualizedTable.errors).length
);

const selectColumnsAsDropdownOptions = createDeepEqualSelector(
    selectVirtualizedTableColumnsKeyedByKey,
    (virtualizedTableColumnsKeyedByKey) =>
        formatDropdownOptions(virtualizedTableColumnsKeyedByKey, {
            // TODO: refactor to key
            keyField: 'value',
            textField: 'label',
            orderByField: 'orderIndex',
            filterPredicate: { selectable: true }
        })
);

const selectVirtualizedTableColumn = createDeepEqualSelector(
    [selectVirtualizedTableColumnsKeyedByKey, selectVirtualizedTableColumnKey],
    (virtualizedTableColumnsKeyedByKey, virtualizedTableColumnKey) => {
        if (virtualizedTableColumnKey) {
            return get(
                virtualizedTableColumnsKeyedByKey,
                virtualizedTableColumnKey
            );
        }
        return undefined;
    }
);

const makeSelectVirtualizedTableColumn =
    (): typeof selectVirtualizedTableColumn => selectVirtualizedTableColumn;

const selectVirtualizedTableColumnFormHeader = createDeepEqualSelector(
    [selectVirtualizedTableColumn],
    (virtualizedTableColumn) => get(virtualizedTableColumn, 'formHeader')
);

const makeSelectVirtualizedTableColumnFormHeader =
    (): typeof selectVirtualizedTableColumnFormHeader =>
        selectVirtualizedTableColumnFormHeader;

const selectVirtualizedTableColumnFormColumnWidth = createDeepEqualSelector(
    [selectVirtualizedTableColumn],
    (virtualizedTableColumn) => get(virtualizedTableColumn, 'formColumnWidth')
);

const selectVirtualizedTableColumnLabel = createDeepEqualSelector(
    [selectVirtualizedTableColumn],
    (virtualizedTableColumn) => get(virtualizedTableColumn, 'label')
);

const makeSelectVirtualizedTableColumnLabel =
    (): typeof selectVirtualizedTableColumnLabel =>
        selectVirtualizedTableColumnLabel;

const selectVirtualizedTableColumnPlaceholder = createDeepEqualSelector(
    [selectVirtualizedTableColumn],
    (virtualizedTableColumn) => get(virtualizedTableColumn, 'placeholder')
);

const makeSelectVirtualizedTableColumnPlaceholder =
    (): typeof selectVirtualizedTableColumnPlaceholder =>
        selectVirtualizedTableColumnPlaceholder;

const makeSelectVirtualizedTableColumnFormColumnWidth =
    (): typeof selectVirtualizedTableColumnFormColumnWidth =>
        selectVirtualizedTableColumnFormColumnWidth;

const selectVirtualizedTableColumnSelectable = createDeepEqualSelector(
    [selectVirtualizedTableColumn],
    (virtualizedTableColumn) => get(virtualizedTableColumn, 'selectable')
);

const selectVirtualizedTableColumnClearable = createDeepEqualSelector(
    [selectVirtualizedTableColumn],
    (virtualizedTableColumn) => get(virtualizedTableColumn, 'clearable')
);

const makeSelectVirtualizedTableColumnSelectable =
    (): typeof selectVirtualizedTableColumnSelectable =>
        selectVirtualizedTableColumnSelectable;

const makeSelectVirtualizedTableColumnClearable =
    (): typeof selectVirtualizedTableColumnClearable =>
    selectVirtualizedTableColumnClearable;

const selectVirtualizedTableColumnType = createDeepEqualSelector(
    [selectVirtualizedTableColumn],
    (virtualizedTableColumn) => get(virtualizedTableColumn, 'type')
);

const makeSelectVirtualizedTableColumnType =
    (): typeof selectVirtualizedTableColumnType =>
        selectVirtualizedTableColumnType;

const selectVirtualizedTableColumnConfirmationModal = createDeepEqualSelector(
    [selectVirtualizedTableColumn],
    (virtualizedTableColumn) =>
        get(virtualizedTableColumn, 'confirmationCheckboxModal')
);

const makeSelectVirtualizedTableColumnConfirmationModal =
    (): typeof selectVirtualizedTableColumnConfirmationModal =>
        selectVirtualizedTableColumnConfirmationModal;

const selectVirtualizedTableColumnOptions = createDeepEqualSelector(
    [makeSelectVirtualizedTableColumn()],
    (virtualizedTableColumn) => {
        return get(virtualizedTableColumn, 'options');
    }
);

const makeSelectVirtualizedTableColumnOptions =
    (): typeof selectVirtualizedTableColumnOptions =>
        selectVirtualizedTableColumnOptions;

const selectVirtualizedTableColumnValue = createDeepEqualSelector(
    [makeSelectVirtualizedTableColumn()],
    (virtualizedTableColumn) => {
        return get(virtualizedTableColumn, 'value');
    }
);

const makeSelectVirtualizedTableColumnValue =
    (): typeof selectVirtualizedTableColumnValue =>
        selectVirtualizedTableColumnValue;

const selectVirtualizedTableColumnHasFooterSummary = createDeepEqualSelector(
    [selectVirtualizedTableColumn],
    (virtualizedTableColumn) => get(virtualizedTableColumn, 'hasFooterSummary')
);

const makeSelectVirtualizedTableColumnHasFooterSummary =
    (): typeof selectVirtualizedTableColumnHasFooterSummary =>
        selectVirtualizedTableColumnHasFooterSummary;

const selectVirtualizedTableColumnUnit = createDeepEqualSelector(
    [selectVirtualizedTableColumn],
    (virtualizedTableColumn) => get(virtualizedTableColumn, 'unit')
);

const makeSelectVirtualizedTableColumnUnit =
    (): typeof selectVirtualizedTableColumnUnit =>
        selectVirtualizedTableColumnUnit;

const selectVirtualizedTableColumnValidators = createDeepEqualSelector(
    [selectVirtualizedTableColumn],
    (virtualizedTableColumn) => get(virtualizedTableColumn, 'validators')
);

const makeSelectVirtualizedTableColumnValidators =
    (): typeof selectVirtualizedTableColumnValidators =>
        selectVirtualizedTableColumnValidators;

const selectVirtualizedTableColumnDisableUpdating = createDeepEqualSelector(
    [selectVirtualizedTableColumn],
    (virtualizedTableColumn) => get(virtualizedTableColumn, 'disableUpdating')
);

const makeSelectVirtualizedTableColumnDisableUpdating =
    (): typeof selectVirtualizedTableColumnDisableUpdating =>
        selectVirtualizedTableColumnDisableUpdating;

const selectVirtualizedTableColumnDisableSorting = createDeepEqualSelector(
    [selectVirtualizedTableColumn],
    (virtualizedTableColumn) => get(virtualizedTableColumn, 'disableSorting')
);

const makeSelectVirtualizedTableColumnDisableSorting =
    (): typeof selectVirtualizedTableColumnDisableSorting =>
        selectVirtualizedTableColumnDisableSorting;

const selectVirtualizedTableColumnListColumns = createDeepEqualSelector(
    [selectVirtualizedTableColumn],
    (virtualizedTableColumn) => get(virtualizedTableColumn, 'listColumns')
);

const makeSelectVirtualizedTableColumnListColumns =
    (): typeof selectVirtualizedTableColumnListColumns =>
        selectVirtualizedTableColumnListColumns;

const makeSelectVirtualizedTableColumnFieldValue = <
    T extends keyof VirtualizedTableColumn
>(
    fieldName: T
): OutputParametricSelector<
    AppState,
    VirtualizedTableProps,
    VirtualizedTableColumn[T],
    (
        arg: ReturnType<typeof selectVirtualizedTableColumn>
    ) => VirtualizedTableColumn[T]
> =>
    createDeepEqualSelector(
        [selectVirtualizedTableColumn],
        (virtualizedTableColumn): VirtualizedTableColumn[T] =>
            get(virtualizedTableColumn, fieldName)
    );

const selectVirtualizedTableColumnWidths = createDeepEqualSelector(
    [selectVirtualizedTable],
    (virtualizedTable) => virtualizedTable.columnWidths
);

const selectVirtualizedTableColumnWidth = createDeepEqualSelector(
    [selectVirtualizedTableColumnWidths, selectVirtualizedTableColumnKey],
    (virtualizedTableColumnWidths, virtualizedTableColumnKey) => {
        if (virtualizedTableColumnKey) {
            return get(virtualizedTableColumnWidths, virtualizedTableColumnKey);
        }
        return undefined;
    }
);

const selectVirtualizedTableColumnWidthOffsets = createDeepEqualSelector(
    [selectVirtualizedTable],
    (virtualizedTable) => virtualizedTable.columnWidthOffsets
);

const selectVirtualizedTableColumnWidthOffset = createDeepEqualSelector(
    [selectVirtualizedTableColumnWidthOffsets, selectVirtualizedTableColumnKey],
    (virtualizedTableColumnWidthOffsets, virtualizedTableColumnKey) => {
        if (virtualizedTableColumnKey) {
            return get(
                virtualizedTableColumnWidthOffsets,
                virtualizedTableColumnKey
            );
        }
        return undefined;
    }
);

const selectVirtualizedTableColumnWidthAdjusted = createDeepEqualSelector(
    [
        selectVirtualizedTableColumnWidth,
        selectVirtualizedTableColumnWidthOffset
    ],
    (
        virtualizedTableColumnWidth = 0,
        virtualizedTableColumnWidthOffset = 0
    ) => {
        const columnWidthAdjusted =
            virtualizedTableColumnWidth + virtualizedTableColumnWidthOffset;
        return columnWidthAdjusted >= VIRTUALIZED_TABLE_COLUMN_MIN_WIDTH
            ? columnWidthAdjusted
            : VIRTUALIZED_TABLE_COLUMN_MIN_WIDTH;
    }
);

const makeSelectVirtualizedTableColumnWidthAdjusted =
    (): typeof selectVirtualizedTableColumnWidthAdjusted =>
        selectVirtualizedTableColumnWidthAdjusted;

const selectVirtualizedTableItemsKeyedByKey = createDeepEqualSelector(
    [selectVirtualizedTable],
    (virtualizedTable) => virtualizedTable.itemsKeyedByKey
);

const selectVirtualizedTableItemArray = createDeepEqualSelector(
    [selectVirtualizedTableItemsKeyedByKey],
    (virtualizedTableItemsKeyedByKey) =>
        Object.values(virtualizedTableItemsKeyedByKey)
);

const selectVirtualizedTableItem = createDeepEqualSelector(
    [selectVirtualizedTableItemsKeyedByKey, selectVirtualizedTableItemKey],
    (virtualizedTableItemsKeyedByKey, virtualizedTableItemKey) => {
        if (virtualizedTableItemKey) {
            return get(
                virtualizedTableItemsKeyedByKey,
                virtualizedTableItemKey
            );
        }
        return undefined;
    }
);

const selectVirtualizedTableSelectedItemsArray = createDeepEqualSelector(
    [selectVirtualizedTableItemsKeyedByKey, selectActiveTableCellRowIds],
    (
        virtualizedTableItemsKeyedByKey,
        activeItemKeys: VirtualizedTableItemKey[]
    ) => {
        return activeItemKeys.map(
            (itemKey) => virtualizedTableItemsKeyedByKey[itemKey]
        );
    }
);

const makeSelectVirtualizedTableItem = (): typeof selectVirtualizedTableItem =>
    selectVirtualizedTableItem;

const selectVirtualizedTableItemValue = createDeepEqualSelector(
    [selectVirtualizedTableItem, selectVirtualizedTableColumnKey],
    (virtualizedTableItem, virtualizedTableColumnKey) => {
        if (virtualizedTableItem && virtualizedTableColumnKey) {
            return get(virtualizedTableItem, virtualizedTableColumnKey);
        }
        return undefined;
    }
);

const makeSelectVirtualizedTableItemValue =
    (): typeof selectVirtualizedTableItemValue =>
        selectVirtualizedTableItemValue;

const selectVirtualizedTableItemValueAsBoolean = createDeepEqualSelector(
    [selectVirtualizedTableItem, selectVirtualizedTableColumnKey],
    (virtualizedTableItem, virtualizedTableColumnKey) => {
        if (virtualizedTableItem && virtualizedTableColumnKey) {
            return Boolean(
                get(virtualizedTableItem, virtualizedTableColumnKey)
            );
        }

        return false;
    }
);

const makeSelectVirtualizedTableItemValueAsBoolean =
    (): typeof selectVirtualizedTableItemValueAsBoolean =>
        selectVirtualizedTableItemValueAsBoolean;

const selectVirtualizedTableError = createDeepEqualSelector(
    [selectVirtualizedTableErrors, selectVirtualizedTableItemKey],
    (virtualizedTableErrors, virtualizedTableItemKey) => {
        if (virtualizedTableItemKey) {
            return get(virtualizedTableErrors, virtualizedTableItemKey);
        }
        return undefined;
    }
);

const selectVirtualizedTableErrorMessage = createDeepEqualSelector(
    [selectVirtualizedTableError, selectVirtualizedTableColumnKey],
    (virtualizedTableError, virtualizedTableColumnKey) => {
        if (virtualizedTableError && virtualizedTableColumnKey) {
            return get(virtualizedTableError, virtualizedTableColumnKey);
        }
        return undefined;
    }
);

const makeSelectVirtualizedTableErrorMessage =
    (): typeof selectVirtualizedTableErrorMessage =>
        selectVirtualizedTableErrorMessage;

const selectVirtualizedTableColumnOption = createDeepEqualSelector(
    [
        selectVirtualizedTableColumnOptionsKeyedByKey,
        selectVirtualizedTableColumnKey,
        selectVirtualizedTableItemValue
    ],
    (
        virtualizedTableColumnOptionsKeyedByKey,
        virtualizedTableColumnKey,
        virtualizedTableItemValue
    ) => {
        if (virtualizedTableItemValue) {
            return get(virtualizedTableColumnOptionsKeyedByKey, [
                virtualizedTableColumnKey,
                virtualizedTableItemValue
            ]);
        }
        return undefined;
    }
);

const selectVirtualizedTableColumnOptionText = createDeepEqualSelector(
    [selectVirtualizedTableColumnOption],
    (virtualizedTableColumnOption) => get(virtualizedTableColumnOption, 'text')
);

const makeSelectVirtualizedTableColumnOptionText =
    (): typeof selectVirtualizedTableColumnOptionText =>
        selectVirtualizedTableColumnOptionText;

const selectVirtualizedTableItemValueOptions = createDeepEqualSelector(
    [
        selectVirtualizedTableColumnOptionsKeyedByKey,
        selectVirtualizedTableColumnKey,
        selectVirtualizedTableItemValue
    ],
    (
        virtualizedTableColumnOptionsKeyedByKey,
        virtualizedTableColumnKey,
        virtualizedTableItemValue
    ) => {
        if (Array.isArray(virtualizedTableItemValue)) {
            return virtualizedTableItemValue.map((optionKey) =>
                get(virtualizedTableColumnOptionsKeyedByKey, [
                    virtualizedTableColumnKey,
                    optionKey
                ])
            );
        }
        return undefined;
    }
);

const selectVirtualizedTableItemValueOptionsText = createDeepEqualSelector(
    [selectVirtualizedTableItemValueOptions],
    (virtualizedTableItemValueOptions) => {
        if (virtualizedTableItemValueOptions) {
            return virtualizedTableItemValueOptions
                .filter((option) => option)
                .map((option) => option.text)
                .join(', ');
        }
        return undefined;
    }
);

const makeSelectVirtualizedTableItemValueOptionsText =
    (): typeof selectVirtualizedTableItemValueOptionsText =>
        selectVirtualizedTableItemValueOptionsText;

const selectVirtualizedTableColumnInitialValues = createDeepEqualSelector(
    [selectVirtualizedTableColumnsArray],
    (virtualizedTableColumnsArray) =>
        virtualizedTableColumnsArray.reduce(
            (
                initialValues: Record<string, VirtualizedTableItemValue>,
                column
            ) => {
                const { initialValue, value } = column;
                const result = { ...initialValues };

                if (initialValue !== undefined && initialValue !== null) {
                    result[value] = initialValue;
                }
                return result;
            },
            {}
        )
);

const selectVirtualizedTableColumnEmptyValueToNull = createDeepEqualSelector(
    [selectVirtualizedTableColumn],
    (virtualizedTableColumn) => get(virtualizedTableColumn, 'emptyValueToNull')
);

const makeSelectVirtualizedTableColumnEmptyValueToNull =
    (): typeof selectVirtualizedTableColumnEmptyValueToNull =>
        selectVirtualizedTableColumnEmptyValueToNull;

const selectVirtualizedTableSubmitHasFailed = createDeepEqualSelector(
    [selectVirtualizedTable],
    (virtualizedTable) =>
        Boolean(get(virtualizedTable, 'submitHasFailed', false))
);

const makeSelectVirtualizedTableSubmitHasFailed =
    (): typeof selectVirtualizedTableSubmitHasFailed =>
        selectVirtualizedTableSubmitHasFailed;

const selectVirtualizedTableNonInitialValueColumnKeys = createDeepEqualSelector(
    selectVirtualizedTableColumnsArray,
    (columns) =>
        columns.reduce(
            (columnKeys: string[], column: VirtualizedTableColumn) =>
                FIELD_TYPE_PASSWORD === column.type
                    ? [...columnKeys, column.value]
                    : columnKeys,
            []
        )
);

const selectVirtualizedTableEditInitialValues = createDeepEqualSelector(
    [
        selectVirtualizedTableItem,
        selectVirtualizedTableNonInitialValueColumnKeys
    ],
    (item, columnKeys) => item && omit(item, columnKeys)
);

const makeSelectVirtualizedTableEditInitialValues =
    (): typeof selectVirtualizedTableEditInitialValues =>
        selectVirtualizedTableEditInitialValues;

export {
    selectVirtualizedTable,
    selectVirtualizedTableColumnKey,
    selectVirtualizedTableItemKey,
    selectVirtualizedTableColumnsArray,
    selectVirtualizedTableColumnsWithValidators,
    selectVirtualizedTableErrors,
    selectVirtualizedTableErrorsExist,
    selectColumnsAsDropdownOptions,
    selectVirtualizedTableColumnKeys,
    selectVirtualizedTableColumn,
    makeSelectVirtualizedTableColumnFormHeader,
    makeSelectVirtualizedTableColumnFormColumnWidth,
    selectVirtualizedTableColumnLabel,
    makeSelectVirtualizedTableColumnLabel,
    selectVirtualizedTableColumnPlaceholder,
    makeSelectVirtualizedTableColumnPlaceholder,
    selectVirtualizedTableColumnSelectable,
    selectVirtualizedTableColumnClearable,
    makeSelectVirtualizedTableColumnSelectable,
    makeSelectVirtualizedTableColumnClearable,
    selectVirtualizedTableColumnType,
    makeSelectVirtualizedTableColumnType,
    selectVirtualizedTableColumnOptions,
    makeSelectVirtualizedTableColumnOptions,
    selectVirtualizedTableColumnValue,
    makeSelectVirtualizedTableColumnValue,
    selectVirtualizedTableColumnValidators,
    makeSelectVirtualizedTableColumnHasFooterSummary,
    makeSelectVirtualizedTableColumnUnit,
    makeSelectVirtualizedTableColumnValidators,
    makeSelectVirtualizedTableColumnDisableUpdating,
    makeSelectVirtualizedTableColumnDisableSorting,
    makeSelectVirtualizedTableColumnListColumns,
    makeSelectVirtualizedTableColumnFieldValue,
    makeSelectVirtualizedTableColumnOptionText,
    makeSelectVirtualizedTableItemValueOptionsText,
    selectVirtualizedTableColumnsKeyedByKey,
    selectVirtualizedTableColumnOptionsKeyedByKey,
    selectVirtualizedTableColumnWidths,
    selectVirtualizedTableColumnWidth,
    selectVirtualizedTableColumnWidthOffsets,
    selectVirtualizedTableColumnWidthOffset,
    selectVirtualizedTableColumnWidthAdjusted,
    makeSelectVirtualizedTableColumnWidthAdjusted,
    selectVirtualizedTableItemsKeyedByKey,
    selectVirtualizedTableItemArray,
    selectVirtualizedTableItem,
    selectVirtualizedTableSelectedItemsArray,
    makeSelectVirtualizedTableItem,
    selectVirtualizedTableItemValue,
    makeSelectVirtualizedTableItemValue,
    makeSelectVirtualizedTableErrorMessage,
    makeSelectVirtualizedTableItemValueAsBoolean,
    makeSelectVirtualizedTableColumnConfirmationModal,
    selectVirtualizedTableColumnInitialValues,
    makeSelectVirtualizedTableColumnEmptyValueToNull,
    makeSelectVirtualizedTableSubmitHasFailed,
    selectVirtualizedTableNonInitialValueColumnKeys,
    makeSelectVirtualizedTableEditInitialValues
};
